/**
 * Client-side resource/points pool ceilings, keyed by champion — the `max`
 * counterpart to the wire's per-tick `current` (#266). AUTO-DERIVED from
 * content (#465), mirroring `catalog.ts`'s (#239) build-time
 * `import.meta.glob` + narrow-schema pattern: the previous hand-kept
 * `CEILINGS_BY_CHAMPION` table silently missed Mage's `mana` pool entirely
 * (only Rogue/Warrior were ever added), so Mage rendered with NO resource bar
 * anywhere (`entityView.ts`'s world-space bar AND `FrameResourceBar.tsx`'s HUD
 * bar) despite `mage.json` authoring a real 100-max mana pool — the exact
 * drift bug class #239/#274/#461 already fixed elsewhere in this file's
 * siblings.
 *
 * WHY THIS EXISTS: the client deliberately does NOT load the full content
 * bundle into the browser (see `abilityLabels.ts`'s doc comment). The wire
 * snapshot carries `resource`/`points` CURRENT balances (`WireEntity.resource`
 * / `.points`, `number | null`) but not `max` — that's static content
 * (`ChampionDef.resource.max` / `ChampionDef.points.max`) and threading it
 * over the wire every tick would be wasteful. Instead this reads the ceiling
 * (and #344's `resourceId`, for bar color-coding) straight off the on-disk
 * champion JSON at BUILD TIME via `import.meta.glob`, narrow-schema-stripped
 * down to `{ id, resource?, points? }` — never anything balance-sensitive
 * (stats/abilities/etc). A champion or kit change needs ZERO edits here.
 */

import { z } from "zod";
import {
  CHAMPION_ID_SCHEMA,
  POINTS_ID_SCHEMA,
  RESOURCE_ID_SCHEMA,
  type ChampionId,
  type ResourceId,
} from "@pit/protocol";

/**
 * A champion's resource-pool ceiling and/or points-pool ceiling. Either half
 * may be `null` (that champion has no such pool).
 *
 *   • `resourceId` (#344) — the content `ResourceDef.id` backing `maxResource`
 *     (e.g. "energy"/"rage"/"mana"), so the resource bar can be COLOR-CODED
 *     (`champions/resourceColors.ts`) instead of one shared fill for every
 *     champion. `null` whenever `maxResource` is `null` (no pool ⇒ no id).
 */
export interface ResourceCeilings {
  readonly maxResource: number | null;
  readonly maxPoints: number | null;
  readonly resourceId: ResourceId | null;
}

const NONE: ResourceCeilings = { maxResource: null, maxPoints: null, resourceId: null };

/**
 * Narrow champion schema: only the pool-ceiling-relevant fields survive the
 * parse. `z.object` (not `strictObject`) strips `stats`/`abilities`/etc, so
 * none of that ever reaches the client bundle — mirrors `catalog.ts`'s
 * `CHAMPION_META_SCHEMA`. `resource`/`points` mirror
 * `RESOURCE_DEF_SCHEMA`/`POINTS_DEF_SCHEMA` (`@pit/protocol`), narrowed to
 * just the ceiling + (for resource) the color-coding id.
 */
const CHAMPION_CEILINGS_SCHEMA = z.object({
  id: CHAMPION_ID_SCHEMA,
  resource: z
    .object({ id: RESOURCE_ID_SCHEMA, max: z.number().int().gte(1) })
    .optional(),
  points: z.object({ id: POINTS_ID_SCHEMA, max: z.number().int().gte(1) }).optional(),
});

/**
 * Build-time map of every on-disk champion JSON → its RAW text, parsed down
 * to `{ id, resource?, points? }`. Same literal-`import.meta.glob` rule as
 * `catalog.ts`: the call MUST be the literal form for Vite's static
 * transform. A malformed champion JSON throws here (loud) — but content is
 * already validated by `@pit/content`'s own tests + the server loader, so
 * this can only fail if that upstream gate was skipped.
 */
function loadContentCeilings(): ReadonlyMap<ChampionId, ResourceCeilings> {
  const modules = import.meta.glob<string>(
    "../../../../packages/content/data/champions/*.json",
    { eager: true, query: "?raw", import: "default" },
  );
  const out = new Map<ChampionId, ResourceCeilings>();
  for (const path of Object.keys(modules).sort()) {
    const raw = modules[path];
    if (raw === undefined) continue;
    const parsed = CHAMPION_CEILINGS_SCHEMA.parse(JSON.parse(raw) as unknown);
    out.set(parsed.id, {
      maxResource: parsed.resource?.max ?? null,
      maxPoints: parsed.points?.max ?? null,
      resourceId: parsed.resource?.id ?? null,
    });
  }
  return out;
}

/** Every content champion's pool ceilings, keyed by its opaque id. */
const CEILINGS_BY_CHAMPION: ReadonlyMap<ChampionId, ResourceCeilings> = loadContentCeilings();

/**
 * Pool ceilings for `championId`, or `{ maxResource: null, maxPoints: null }`
 * for an unmapped champion (fail-safe: renders neither bar/pips). Pure
 * lookup.
 */
export function resourceCeilings(championId: ChampionId): ResourceCeilings {
  return CEILINGS_BY_CHAMPION.get(championId) ?? NONE;
}
